import React from 'react';
import Button from './ui/Button/Button';
import { isChunkLoadError } from '../utils/lazyWithReload';
import '../styles/AppErrorBoundary.css';

/**
 * Around every page (App.js). Before it, one page that threw while
 * rendering took the whole app down: React unmounts the tree and only the
 * olive page background was left, with no word of what happened. Now the
 * page says so and offers a reload; a page file that could not load (a tab
 * from before a deploy, once `lazyWithReload`'s one reload has been tried)
 * says a newer version is out.
 *
 * The crash is reported with console.error, which the iOS WebView's issue
 * reporter forwards. `resetKey` (the path) clears it: moving to another page
 * tries again.
 *
 * reload  injectable for tests
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, key: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    return props.resetKey !== state.key
      ? { error: null, key: props.resetKey }
      : null;
  }

  componentDidCatch(error, info) {
    console.error('[app] a page crashed:', error, info?.componentStack ?? '');
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const reload = this.props.reload ?? (() => window.location.reload());
    return (
      <div className="app-error" role="alert">
        <div className="app-error-content">
          <p className="app-error-message">
            {isChunkLoadError(error)
              ? 'A newer version of Housfully is out. Reload to use it.'
              : 'Something went wrong on this page.'}
          </p>
          <Button variant="primary" onClick={reload}>
            Reload
          </Button>
        </div>
      </div>
    );
  }
}
